import { Outlet } from "react-router";
import { useTranslation } from "react-i18next";
import { PageHeader, SectionNav } from "../../components/ui";

/*
 * Profile and Security, as two sections of one account rather than two pages
 * that happen to be personal.
 *
 * They were already siblings — both under `/settings`, both reached from the
 * menu under your own name, both meaning the same thing whichever organization
 * the switcher holds — and the only way to get from one to the other was back
 * up through that menu. A customer who has just turned on two-factor and wants
 * to fix the spelling of their name had no link to follow.
 *
 * The URLs do not move. This is a pathless layout route, so `/settings/profile`
 * and `/settings/security` are still exactly that: every bookmark, the two user
 * menu entries and the `/settings/account` redirect all keep working, and
 * nothing had to be added to the redirect list to pay for the new shape.
 *
 * What does *not* belong here is anything scoped to an organization. Somebody
 * in three organizations has one profile and three sets of billing details, so
 * Team, API keys and Billing stay in the rail where switching organizations
 * visibly changes what they mean.
 */
export function AccountLayout() {
  const { t } = useTranslation("profile");
  const sections = [
    { to: "/settings/profile", label: t("account.profile") },
    { to: "/settings/security", label: t("account.security") },
  ];
  return (
    <div className="space-y-6">
      <PageHeader
        title={t("account.title")}
        description={t("account.lead")}
      />

      {/*
        A fixed 11rem for the section list rather than a fraction of the page.
        As `1fr` it grew with the viewport, so on a wide screen two short words
        sat in a column half as wide as the form beside them.
      */}
      <div className="grid gap-6 lg:grid-cols-[11rem_minmax(0,1fr)] lg:gap-10">
        {/* Sticks to the viewport rather than scrolling away: the document is
            what scrolls here — `main` sets no overflow — so `top` is measured
            against the window, and the list stays put while a long section
            moves under it. */}
        <div className="lg:sticky lg:top-10 lg:self-start">
          <SectionNav items={sections} />
        </div>

        {/* `min-w-0` because a grid child will not shrink below its longest
            unbreakable word, and these sections hold email addresses and
            recovery codes. Without it the column sets a floor wider than the
            page and the whole thing scrolls sideways. */}
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
